import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 8000,
});

// ─── Static fallback data ─────────────────────────────────────────────────────
// Used when backend is unavailable (e.g. offline, backend down, Render cold start)

const FALLBACK_SETTINGS = {
  company: {
    name: 'Codinity',
    tagline: 'Premium Software Development',
    email: 'hello@codinity.com',
    careersEmail: 'careers@codinity.com',
    phone: '+1 (234) 567-890',
    workingHours: 'Monday - Friday, 9am - 6pm',
    timezone: 'GMT+5',
    availability: 'Currently accepting new projects',
    responseTime: 'Within 24 hours',
  },
  hero: {
    headline: 'Where Innovation Meets Execution',
    subheadline: 'Transforming ideas into scalable, high-performance software solutions. We craft mobile apps, web platforms, and AI-powered systems that drive business growth.',
    badge: 'Premium Software Development',
    primaryCTA: 'Start Your Project',
    secondaryCTA: 'View Our Services',
  },
  about: {
    title: 'Innovating the Future of Technology',
    description: 'Codinity is a forward-thinking software development company dedicated to creating cutting-edge digital solutions.',
    mission: 'Our mission is to empower businesses with scalable, reliable, and performance-optimized software that drives growth and competitive advantage.',
  },
  social: {
    linkedin: 'https://linkedin.com/company/codinity',
    github: 'https://github.com/codinity',
    twitter: '',
    facebook: '',
  },
  careers: {
    heroTitle: 'Build Your Career',
    heroSubtitle: 'With Codinity',
    heroDescription: 'Join a team of passionate innovators building cutting-edge solutions.',
  },
};

const FALLBACK_METRICS = [
  { value: '150+', label: 'Projects Delivered' },
  { value: '80+', label: 'Happy Clients' },
  { value: '5+', label: 'Years Experience' },
  { value: '98%', label: 'Client Satisfaction' },
];

const FALLBACK_INDUSTRIES = [
  { title: 'Healthcare & Wellness', description: 'Digital health platforms, telemedicine apps, and wellness tools that improve patient outcomes.' },
  { title: 'Fintech & Payments', description: 'Secure payment gateways, digital wallets, and financial management platforms.' },
  { title: 'E-commerce & Retail', description: 'High-performance storefronts, inventory systems, and personalized shopping experiences.' },
  { title: 'SaaS & Productivity', description: 'Scalable cloud applications, team collaboration tools, and automation platforms.' },
  { title: 'EdTech & Learning', description: 'Interactive learning management systems, virtual classrooms, and adaptive education platforms.' },
  { title: 'Logistics & Operations', description: 'Fleet tracking, supply chain optimization, and operations management systems.' },
];

const FALLBACK_TECH_STACK = [
  { slug: 'react', name: 'React', color: '61DAFB' },
  { slug: 'flutter', name: 'Flutter', color: '54C5F8' },
  { slug: 'nextdotjs', name: 'Next.js', color: 'ffffff' },
  { slug: 'nodedotjs', name: 'Node.js', color: '5FA04E' },
  { slug: 'typescript', name: 'TypeScript', color: '3178C6' },
  { slug: 'python', name: 'Python', color: 'FFD43B' },
  { slug: 'firebase', name: 'Firebase', color: 'FFCA28' },
  { slug: 'postgresql', name: 'PostgreSQL', color: '4169E1' },
  { slug: 'mongodb', name: 'MongoDB', color: '47A248' },
  { slug: 'docker', name: 'Docker', color: '2496ED' },
  { slug: 'figma', name: 'Figma', color: 'F24E1E' },
  { slug: 'tailwindcss', name: 'Tailwind CSS', color: '06B6D4' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const withFallback = async (fetchFn, fallbackData) => {
  try {
    return await fetchFn();
  } catch {
    return fallbackData;
  }
};

// ─── Public API fetches (with fallback) ───────────────────────────────────────

export const getSettings = () =>
  withFallback(
    async () => {
      const res = await api.get('/settings/public');
      return res.data.data;
    },
    FALLBACK_SETTINGS
  );

export const getMetrics = () =>
  withFallback(
    async () => {
      const res = await api.get('/metrics');
      return res.data.data;
    },
    FALLBACK_METRICS
  );

export const getIndustries = () =>
  withFallback(
    async () => {
      const res = await api.get('/industries');
      return res.data.data;
    },
    FALLBACK_INDUSTRIES
  );

export const getTechStack = () =>
  withFallback(
    async () => {
      const res = await api.get('/tech-stack');
      return res.data.data;
    },
    FALLBACK_TECH_STACK
  );

// Jobs are real data — never substitute placeholders. An unreachable backend
// must render the same "no open positions" state as an empty jobs collection.
export const getActiveJobs = () =>
  withFallback(
    async () => {
      const res = await api.get('/jobs');
      return Array.isArray(res.data.data) ? res.data.data : [];
    },
    []
  );

// ─── Form submissions (no fallback — actual mutations) ────────────────────────

export const submitInquiry = (data) => api.post('/inquiries', data);

export const submitApplication = (data) =>
  api.post('/applications', data, { timeout: 30000 });

// ─── Blog ─────────────────────────────────────────────────────────────────────
// Published posts only. Listing returns summaries (no blocks); an unreachable
// backend yields an empty list, same as "no posts yet".
export const getBlogPosts = () =>
  withFallback(
    async () => {
      const res = await api.get('/blog/posts');
      return Array.isArray(res.data.data) ? res.data.data : [];
    },
    []
  );

// Single post by slug. Returns null when missing/unpublished/unreachable so the
// page can render a clean "not found" state.
export const getBlogPostBySlug = (slug) =>
  withFallback(
    async () => {
      const res = await api.get(`/blog/posts/${encodeURIComponent(slug)}`);
      return res.data.data;
    },
    null
  );

export default api;
